import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faEnvelope, faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import copy from "copy-to-clipboard";

import ViewportContext from "~/common/ViewportContext";
import { ReactComponent as TeamsLogo } from "~/legacy-assets/images/logos/microsoft-teams-icon.svg";
import { ReactComponent as SlackLogo } from "~/legacy-assets/images/logos/slack_icon_only.svg";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button, toast, useModals } from "~/ui";
import { HelpCenterIcon } from "~/ui/legacyFellowIndex";
import { Menu } from "~/ui/menu";
import { useHideDuringAndAfterTour } from "~/welcomeChecklist";

import StreamSettingsModal from "../../StreamSettings";

import type { BannersCancelMeetingMutation } from "~/__generated__/BannersCancelMeetingMutation.graphql";
import type { BannersDismissIllustrationMutation } from "~/__generated__/BannersDismissIllustrationMutation.graphql";
import type { BannersDismissMutation } from "~/__generated__/BannersDismissMutation.graphql";
import type { BannersRequestCancelMeetingQuery } from "~/__generated__/BannersRequestCancelMeetingQuery.graphql";

import styles from "./Banners.module.css";

const cancelMeetingMutation = graphql`
	mutation BannersCancelMeetingMutation($noteId: ID!) {
		automationTimeSaverCancelMeeting(noteId: $noteId) {
			note {
				viewerShowAutomationTimeSaverBanner
			}
		}
	}
`;

const dismissBannerMutation = graphql`
	mutation BannersDismissMutation($noteId: ID!) {
		dismissAutomationTimeSaverBanner(noteId: $noteId) {
			note {
				viewerShowAutomationTimeSaverBanner
			}
		}
	}
`;

const dismissIllustrationMutation = graphql`
	mutation BannersDismissIllustrationMutation($streamId: ID!) {
		dismissAutomationTimeSaverIllustration(streamId: $streamId) {
			stream {
				viewerDismissedAutomationTimeSaverIllustration
			}
		}
	}
`;

const requestCancelMeetingQuery = graphql`
	query BannersRequestCancelMeetingQuery($noteId: ID!) {
		note: node(id: $noteId) {
			... on Note {
				event {
					organizerEmail
					organizer {
						fullName
						email
						internalAttributes {
							slackDmLink
							teamsDmLink
						}
					}
				}
			}
		}
	}
`;

function dismissBanner(noteId: string, callback?: () => void) {
	commitMutation<BannersDismissMutation>(environment, {
		mutation: dismissBannerMutation,
		variables: { noteId },
		onCompleted: ({ dismissAutomationTimeSaverBanner }, err) => {
			callback?.();

			if (err?.length || !dismissAutomationTimeSaverBanner) {
				toast.error(err?.[0]?.message || "Failed to dismiss banner");
			}
		},
	});
}

export function TimeSaver({ streamId }: { streamId: string }) {
	const [loading, setLoading] = React.useState<boolean>(false);

	React.useEffect(() => {
		dispatchAnalyticsTrack("Automation Banner Shown", {
			banner: "time_saver",
		});
	}, []);

	const modalCtx = useModals();
	const onClose = React.useCallback(() => {
		if (loading) return;

		setLoading(true);

		commitMutation<BannersDismissIllustrationMutation>(environment, {
			mutation: dismissIllustrationMutation,
			variables: { streamId },
			onCompleted: ({ dismissAutomationTimeSaverIllustration }, err) => {
				setLoading(false);

				if (err?.length || !dismissAutomationTimeSaverIllustration) {
					toast.error(err?.[0]?.message || "Failed to dismiss banner");
				}
				dispatchAnalyticsTrack("Automation Banner Dismissed", {
					banner: "time_saver",
				});
			},
		});
	}, [streamId, loading]);

	const onOpenSettings = React.useCallback(() => {
		dispatchAnalyticsTrack("Automation Intent", {
			banner: "time_saver",
			source: "automation_banner",
		});
		modalCtx?.open(({ close }) => (
			<StreamSettingsModal close={close} streamId={streamId} defaultSection="automations" />
		));
	}, [modalCtx, streamId]);

	const { isMobile } = React.useContext(ViewportContext);

	const { hiddenStyles } = useHideDuringAndAfterTour();

	return (
		<article className={styles.container} style={hiddenStyles}>
			<div className={styles.content}>
				<h1>
					Would you like to set up <b>time-saving automations</b> for this meeting?
				</h1>
			</div>
			{!isMobile && (
				<HelpCenterIcon
					articleId="5650922"
					title="Set up no agenda meeting automations"
					className={styles.iconButton}
				/>
			)}
			<div className={styles.actions}>
				<Button size="slim" variant="primary" className={styles.button} onClick={onOpenSettings}>
					Yes
				</Button>
				<Button
					size="slim"
					variant="secondary"
					className={cx(styles.button, styles.secondary)}
					onClick={onClose}
					disabled={loading}
				>
					No
				</Button>
			</div>
		</article>
	);
}

export function CancelMeeting({ noteId, minutes }: { noteId: string; minutes: number }) {
	const [loading, setLoading] = React.useState<boolean>(false);
	const resetLoading = React.useCallback(() => setLoading(false), []);

	React.useEffect(() => {
		dispatchAnalyticsTrack("Automation Banner Shown", {
			banner: "cancel_meeting",
		});
	}, []);

	const onDismiss = React.useCallback(() => {
		if (loading) return;

		setLoading(true);
		dismissBanner(noteId, resetLoading);

		dispatchAnalyticsTrack("Automation Banner Dismissed", {
			banner: "cancel_meeting",
		});
	}, [noteId, loading, resetLoading]);

	const onCancel = React.useCallback(() => {
		if (loading) return;

		setLoading(true);

		commitMutation<BannersCancelMeetingMutation>(environment, {
			mutation: cancelMeetingMutation,
			variables: { noteId },
			onCompleted: ({ automationTimeSaverCancelMeeting }, err) => {
				setLoading(false);

				if (err?.length || !automationTimeSaverCancelMeeting) {
					toast.error(err?.[0]?.message || "Failed to cancel meeting");
				} else {
					toast.success("Meeting cancelled successfully");
				}
			},
		});
	}, [noteId, loading]);

	const { hiddenStyles } = useHideDuringAndAfterTour();

	return (
		<article className={styles.container} style={hiddenStyles}>
			<div className={styles.content}>
				<h1>
					Looks like there&apos;s no agenda. Cancel this meeting to give everyone <b>{minutes} minutes</b> back?
				</h1>
			</div>
			<div className={styles.actions}>
				<Button size="slim" variant="primary" className={styles.button} onClick={onCancel} disabled={loading}>
					Cancel meeting
				</Button>
				<Button
					size="slim"
					variant="secondary"
					className={cx(styles.button, styles.secondary)}
					onClick={onDismiss}
					disabled={loading}
				>
					Dismiss
				</Button>
			</div>
		</article>
	);
}

export function RequestCancelMeeting({ noteId }: { noteId: string }) {
	const props = useLazyLoadQuery<BannersRequestCancelMeetingQuery>(
		requestCancelMeetingQuery,
		{ noteId },
		{ fetchPolicy: "network-only" },
	);

	React.useEffect(() => {
		dispatchAnalyticsTrack("Automation Banner Shown", {
			banner: "request_cancel",
		});
	}, []);

	const [loading, setLoading] = React.useState<boolean>(false);
	const resetLoading = React.useCallback(() => setLoading(false), []);

	const email = props?.note?.event?.organizer?.email ?? props?.note?.event?.organizerEmail;
	const slackLink = props?.note?.event?.organizer?.internalAttributes?.slackDmLink;
	const teamsLink = props?.note?.event?.organizer?.internalAttributes?.teamsDmLink;

	const onCopyEmail = React.useCallback(() => {
		if (!email) return;

		copy(email);
		toast.success("Copied to clipboard");
	}, [email]);

	const onOpenSlack = React.useCallback(() => {
		if (!slackLink) return;

		window.open(slackLink, "_blank");
	}, [slackLink]);

	const onOpenMSTeams = React.useCallback(() => {
		if (!teamsLink) return;

		window.open(teamsLink, "_blank");
	}, [teamsLink]);

	const onDismiss = React.useCallback(() => {
		if (loading) return;

		setLoading(true);
		dismissBanner(noteId, resetLoading);

		dispatchAnalyticsTrack("Automation Banner Dismissed", {
			banner: "request_cancel",
		});
	}, [noteId, loading, resetLoading]);

	const { hiddenStyles } = useHideDuringAndAfterTour();

	return (
		<article className={styles.container} style={hiddenStyles}>
			<div className={styles.content}>
				<h1>Looks like this meeting has no agenda. Send a message to the organizer requesting to cancel?</h1>
			</div>

			<Menu mobileHeaderTitle="Request cancellation">
				<Menu.Trigger asChild>
					<Button variant="primary" size="slim" className={styles.button}>
						Request cancellation
					</Button>
				</Menu.Trigger>
				<Menu.Content>
					{email && (
						<Menu.Item onSelect={onCopyEmail}>
							<span className={styles.menuItem}>
								<FontAwesomeIcon fixedWidth icon={faEnvelope} className={styles.menuItemIcon} />
								Copy email
							</span>
						</Menu.Item>
					)}
					{slackLink && (
						<Menu.Item onSelect={onOpenSlack}>
							<span className={styles.menuItem}>
								<SlackLogo className={styles.menuItemIcon} />
								Direct message in Slack
							</span>
						</Menu.Item>
					)}
					{teamsLink && (
						<Menu.Item onSelect={onOpenMSTeams}>
							<span className={styles.menuItem}>
								<TeamsLogo className={styles.menuItemIcon} />
								Chat in Teams
							</span>
						</Menu.Item>
					)}
				</Menu.Content>
			</Menu>

			<Button variant="secondary" size="slim" className={styles.button} onClick={onDismiss} disabled={loading}>
				Dismiss
			</Button>
		</article>
	);
}

export function NoteLocked({ canUnlockNote }: { canUnlockNote: boolean }) {
	return (
		<article className={cx(styles.container, styles.lockedNoteBanner)}>
			<div className={styles.content}>
				<FontAwesomeIcon size="xs" fixedWidth icon={faLock} className={styles.lockIcon} />
				<h1>
					Note is locked: You can mark items as done, react, and comment.{" "}
					{canUnlockNote ? "Unlock to make edits." : "Ask an admin to unlock the note to make edits."}
				</h1>
			</div>
		</article>
	);
}

export function NoteReadOnly() {
	return (
		<article className={cx(styles.container, styles.lockedNoteBanner)}>
			<div className={styles.content}>
				<FontAwesomeIcon size="xs" fixedWidth icon={faLock} className={styles.lockIcon} />
				<h1>You have view-only access to this Note</h1>
			</div>
		</article>
	);
}
